import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import {
  getReceivedOffers,
  acceptOffer,
  rejectOffer,
  clearReceivedOffers,
  clearError,
} from "../receivedOffersSlice";
import { getServiceRequest } from "../customerServicingSlice";
import { ReceivedOffersState, OfferStatusCode } from "../models/receivedOffersModels";
import { ServiceRequestsState } from "../models/models";
import { CompletionTimeCode, WarrantyPeriodCode } from "../../serviceOffers/models/models";
import Loader from "../../../shared/components/Loader";
import PageBreadcrumbs from "../../../shared/components/PageBreadcrumbs";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import OfferComparisonTable from "./OfferComparisonTable";
import OfferComparisonGrid from "./OfferComparisonGrid";
import colors from "../../../shared/themes/colors";

type SortOption = "bestMatch" | "priceLow" | "priceHigh" | "timeline" | "warranty";

const completionTimeOrder: Record<number, number> = {
  [CompletionTimeCode.sameDay]: 1,
  [CompletionTimeCode.oneToTwoDays]: 2,
  [CompletionTimeCode.threeToFiveDays]: 3,
  [CompletionTimeCode.oneWeek]: 4,
  [CompletionTimeCode.twoWeeks]: 5,
  [CompletionTimeCode.custom]: 6,
};

const warrantyOrder: Record<number, number> = {
  [WarrantyPeriodCode.thirtyDays]: 1,
  [WarrantyPeriodCode.ninetyDays]: 2,
  [WarrantyPeriodCode.sixMonths]: 3,
  [WarrantyPeriodCode.oneYear]: 4,
  [WarrantyPeriodCode.custom]: 5,
};

const CompareOffersPage = () => {
  const { id } = useParams();
  const { translate } = useTranslation(["servicing", "serviceOffers", "common"]);
  const dispatch = useAppDispatch();

  const [viewMode, setViewMode] = useState<"table" | "grid">("grid");
  const [sortBy, setSortBy] = useState<SortOption>("bestMatch");
  const [hideExpired, setHideExpired] = useState(true);
  const [partsIncludedOnly, setPartsIncludedOnly] = useState(false);

  const { receivedOffers, loading, actionLoading, error } =
    useAppSelector<ReceivedOffersState>(
      (state: any) => state.customerServiceReceivedOffers
    );
  const { serviceRequest } = useAppSelector<ServiceRequestsState>(
    (state: any) => state.customerServicing
  );

  useEffect(() => {
    if (id) {
      dispatch(getReceivedOffers(id));
      dispatch(getServiceRequest(id));
    }

    return () => {
      dispatch(clearReceivedOffers());
    };
  }, [id, dispatch]);

  const handleAccept = async (offerId: string) => {
    if (!id) return;
    await dispatch(acceptOffer({ serviceRequestId: id, offerId }));
    dispatch(getReceivedOffers(id));
    dispatch(getServiceRequest(id));
  };

  const handleReject = async (offerId: string, reason?: string) => {
    if (!id) return;
    await dispatch(rejectOffer({ serviceRequestId: id, offerId, reason }));
    dispatch(getReceivedOffers(id));
    dispatch(getServiceRequest(id));
  };

  const filteredOffers = useMemo(() => {
    let offers = [...(receivedOffers?.items || [])];

    if (hideExpired) {
      offers = offers.filter((o) => o.statusCode !== OfferStatusCode.expired);
    }

    if (partsIncludedOnly) {
      offers = offers.filter((o) => o.includePartsAndMaterials);
    }

    switch (sortBy) {
      case "priceLow":
        offers.sort((a, b) => a.totalEstimatedCost - b.totalEstimatedCost);
        break;
      case "priceHigh":
        offers.sort((a, b) => b.totalEstimatedCost - a.totalEstimatedCost);
        break;
      case "timeline":
        offers.sort((a, b) => {
          const aOrder = a.completionTimeCode ? (completionTimeOrder[a.completionTimeCode] || 99) : 99;
          const bOrder = b.completionTimeCode ? (completionTimeOrder[b.completionTimeCode] || 99) : 99;
          return aOrder - bOrder;
        });
        break;
      case "warranty":
        offers.sort((a, b) => {
          const aOrder = a.warrantyPeriodCode ? (warrantyOrder[a.warrantyPeriodCode] || 99) : 99;
          const bOrder = b.warrantyPeriodCode ? (warrantyOrder[b.warrantyPeriodCode] || 99) : 99;
          return bOrder - aOrder;
        });
        break;
      case "bestMatch":
      default:
        break;
    }

    return offers;
  }, [receivedOffers, hideExpired, partsIncludedOnly, sortBy]);

  if (loading) {
    return <Loader />;
  }

  const breadcrumbItems = [
    { title: translate("servicing:myServiceRequests"), link: "/service-requests" },
    {
      title: serviceRequest?.internalNumber || "",
      link: `/service-board/${id}`,
    },
    { title: translate("servicing:compareOffers") },
  ];

  return (
    <>
      <Toolbar className="flex items-center justify-between w-full">
        <Box className="heading-2">
          <PageBreadcrumbs items={breadcrumbItems} />
        </Box>
      </Toolbar>

      <Box
        sx={{
          flex: 1,
          width: "100%",
          background: colors.secondary.gray96,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box sx={{ flex: 1, maxWidth: "1080px", py: 2 }}>
          <ActionToast
            open={!!error}
            variant="error"
            message={translate(NotificationMessage.error(error).message)}
            onClose={() => dispatch(clearError())}
            duration={6000}
          />

          {/* Filters Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Sort Dropdown */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  className="text-12-regular"
                  sx={{ color: colors.secondary.gray40, textTransform: "uppercase", whiteSpace: "nowrap" }}
                >
                  {translate("servicing:sortBy")}
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    sx={{ fontSize: "14px" }}
                  >
                    <MenuItem value="bestMatch">{translate("servicing:bestMatch")}</MenuItem>
                    <MenuItem value="priceLow">{translate("servicing:priceLowToHigh")}</MenuItem>
                    <MenuItem value="priceHigh">{translate("servicing:priceHighToLow")}</MenuItem>
                    <MenuItem value="timeline">{translate("serviceOffers:timeline")}</MenuItem>
                    <MenuItem value="warranty">{translate("serviceOffers:warrantyPeriod")}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Filters */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideExpired}
                    onChange={(e) => setHideExpired(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box className="text-14-regular" sx={{ color: colors.secondary.gray30 }}>
                    {translate("servicing:hideExpired")}
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={partsIncludedOnly}
                    onChange={(e) => setPartsIncludedOnly(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box className="text-14-regular" sx={{ color: colors.secondary.gray30 }}>
                    {translate("servicing:partsIncludedOnly")}
                  </Box>
                }
              />
            </Box>

            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, v) => v && setViewMode(v)}
              size="small"
            >
              <ToggleButton value="grid">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table">
                <TableRowsIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Content */}
          {viewMode === "table" ? (
            <OfferComparisonTable
              offers={filteredOffers}
              onAccept={handleAccept}
              onReject={handleReject}
              actionLoading={actionLoading}
            />
          ) : (
            <OfferComparisonGrid
              offers={filteredOffers}
              onAccept={handleAccept}
              onReject={handleReject}
              actionLoading={actionLoading}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default CompareOffersPage;
